import { queryOptions, useMutation } from '@tanstack/react-query';

import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';

import {
  AspirationPoolDonorResponse,
  AspirationPoolSummaryResponse,
  AspirationTodayResponse,
  AvgsResponse,
  CreateAspPoolRequest,
  EmbryologistDataEntryRequest,
  FertilizationDataResponse,
  OpuEntriesResponse,
  OpuStatsResponse,
  PoolResponse,
  QcDataResponse,
  QcDataUpdateRequest,
  RemoveAspPoolRequest,
} from './types';

export type {
  AspirationPoolDonorResponse,
  AspirationPoolSummaryResponse,
  AspirationTodayResponse,
  AvgsResponse,
  CreateAspPoolRequest,
  EmbryologistDataEntryRequest,
  FertilizationDataResponse,
  OpuEntriesResponse,
  OpuStatsResponse,
  PoolResponse,
  QcDataResponse,
  QcDataUpdateRequest,
  RemoveAspPoolRequest,
} from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(token: AccessToken): HeadersInit {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function fetchGet<T>(
  token: AccessToken,
  path: string,
  params?: URLSearchParams,
): Promise<T> {
  const url = params
    ? `${getBaseUrl()}/api/v1/opu-search/${path}?${params.toString()}`
    : `${getBaseUrl()}/api/v1/opu-search/${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchMutation<T>(
  token: AccessToken,
  path: string,
  method: 'POST' | 'PUT' | 'DELETE',
  body?: unknown,
  params?: URLSearchParams,
): Promise<T> {
  const baseUrl = `${getBaseUrl()}/api/v1/opu-search/${path}`;
  const url = params ? `${baseUrl}?${params.toString()}` : baseUrl;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(token),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchCompletedAspirations(
  token: AccessToken,
  params: {
    date: string;
    siteId: string;
  },
): Promise<AspirationTodayResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('date', params.date);
  searchParams.set('siteId', params.siteId);
  return fetchGet<AspirationTodayResponse[]>(
    token,
    'aspirations/completed',
    searchParams,
  );
}

async function fetchCompletedAspirationsFbd(
  token: AccessToken,
  params: {
    date: string;
    siteId: string;
    location: string;
  },
): Promise<AspirationTodayResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('date', params.date);
  searchParams.set('siteId', params.siteId);
  searchParams.set('location', params.location);
  return fetchGet<AspirationTodayResponse[]>(
    token,
    'aspirations/completed/fbd',
    searchParams,
  );
}

async function fetchFertData(
  token: AccessToken,
  params: {
    donorId: string;
    ranch?: string | null;
  },
): Promise<FertilizationDataResponse[]> {
  const searchParams = new URLSearchParams();
  if (params.ranch) searchParams.set('ranch', params.ranch);
  return fetchGet<FertilizationDataResponse[]>(
    token,
    `fert-data/${params.donorId}`,
    searchParams.toString() ? searchParams : undefined,
  );
}

async function fetchStats(
  token: AccessToken,
  params: {
    aspirationDate: string;
    siteId: string;
    location?: string | null;
  },
): Promise<OpuStatsResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  if (params.location) searchParams.set('location', params.location);
  return fetchGet<OpuStatsResponse>(token, 'stats', searchParams);
}

async function fetchStatsPse(
  token: AccessToken,
  params: {
    aspirationDate: string;
    siteId: string;
    location?: string | null;
  },
): Promise<OpuStatsResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  if (params.location) searchParams.set('location', params.location);
  return fetchGet<OpuStatsResponse>(token, 'stats/pse', searchParams);
}

async function fetchEntries(
  token: AccessToken,
  params: {
    aspirationDate: string;
    siteId: string;
    techId: string;
  },
): Promise<OpuEntriesResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  searchParams.set('techId', params.techId);
  return fetchGet<OpuEntriesResponse[]>(token, 'entries', searchParams);
}

async function fetchEntriesPse(
  token: AccessToken,
  params: {
    aspirationDate: string;
    siteId: string;
    pseId: string;
  },
): Promise<OpuEntriesResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  searchParams.set('pseId', params.pseId);
  return fetchGet<OpuEntriesResponse[]>(token, 'entries/pse', searchParams);
}

async function fetchTechAverages(
  token: AccessToken,
  params: {
    aspirationDate: string;
    siteId: string;
    techId: string;
  },
): Promise<AvgsResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  searchParams.set('techId', params.techId);
  return fetchGet<AvgsResponse[]>(token, 'tech-averages', searchParams);
}

async function fetchAverages(
  token: AccessToken,
  params: {
    aspirationDate: string;
    siteId: string;
  },
): Promise<AvgsResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  return fetchGet<AvgsResponse[]>(token, 'averages', searchParams);
}

async function fetchPool(
  token: AccessToken,
  params: {
    aspirationDate: string;
    siteId: string;
  },
): Promise<PoolResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  return fetchGet<PoolResponse[]>(token, 'pool', searchParams);
}

async function fetchPoolMultiSite(
  token: AccessToken,
  params: {
    aspirationDate: string;
    siteIds: string[];
  },
): Promise<AspirationPoolDonorResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  for (const siteId of params.siteIds) {
    searchParams.append('siteIds', siteId);
  }
  return fetchGet<AspirationPoolDonorResponse[]>(
    token,
    'pool/multi-site',
    searchParams,
  );
}

async function fetchQcData(
  token: AccessToken,
  params: {
    date: string;
    siteId: string;
    location?: string | null;
  },
): Promise<QcDataResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('date', params.date);
  searchParams.set('siteId', params.siteId);
  if (params.location) searchParams.set('location', params.location);
  return fetchGet<QcDataResponse>(token, 'qc', searchParams);
}

async function fetchShowStopSign(
  token: AccessToken,
  siteId: string,
): Promise<boolean> {
  return fetchGet<boolean>(token, `stop-sign/${siteId}`);
}

async function refreshStopGoSignFn(
  token: AccessToken,
  params: {
    siteId: string;
    val: string;
  },
): Promise<boolean> {
  const searchParams = new URLSearchParams();
  searchParams.set('val', params.val);
  return fetchMutation<boolean>(
    token,
    `stop-sign/${params.siteId}`,
    'PUT',
    undefined,
    searchParams,
  );
}

async function updateAspirationDataEntryFn(
  token: AccessToken,
  params: {
    input: EmbryologistDataEntryRequest;
    isReturn: string;
  },
): Promise<boolean> {
  const searchParams = new URLSearchParams();
  searchParams.set('isReturn', params.isReturn);
  return fetchMutation<boolean>(
    token,
    'aspirations/data-entry',
    'PUT',
    params.input,
    searchParams,
  );
}

async function createAspPoolFn(
  token: AccessToken,
  request: CreateAspPoolRequest,
): Promise<string> {
  return fetchMutation<string>(token, 'pools', 'POST', request);
}

async function removeAspPoolFn(
  token: AccessToken,
  request: RemoveAspPoolRequest,
): Promise<boolean> {
  return fetchMutation<boolean>(token, 'pools', 'DELETE', request);
}

async function fetchAspPools(
  token: AccessToken,
  poolNumbers: number[],
): Promise<AspirationPoolSummaryResponse[]> {
  const searchParams = new URLSearchParams();
  for (const poolNumber of poolNumbers) {
    searchParams.append('poolNumbers', String(poolNumber));
  }
  return fetchGet<AspirationPoolSummaryResponse[]>(
    token,
    'pools',
    searchParams,
  );
}

async function updateQcDataFn(
  token: AccessToken,
  params: {
    input: QcDataUpdateRequest;
    aspirationDate: string;
    siteId: string;
    location?: string | null;
  },
): Promise<boolean> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  if (params.location) searchParams.set('location', params.location);
  return fetchMutation<boolean>(token, 'qc', 'PUT', params.input, searchParams);
}

export const completedAspirationsOptions = (
  token: AccessToken,
  params: {
    date: string;
    siteId: string;
  },
) =>
  queryOptions({
    queryKey: ['opu-search', 'aspirations-completed', params],
    enabled: !!token,
    async queryFn() {
      return fetchCompletedAspirations(token, params);
    },
  });

export const completedAspirationsFbdOptions = (
  token: AccessToken,
  params: {
    date: string;
    siteId: string;
    location: string;
  },
) =>
  queryOptions({
    queryKey: ['opu-search', 'aspirations-completed-fbd', params],
    enabled: !!token,
    async queryFn() {
      return fetchCompletedAspirationsFbd(token, params);
    },
  });

export const fertDataOptions = (
  token: AccessToken,
  params: {
    donorId: string;
    ranch?: string | null;
  },
) =>
  queryOptions({
    queryKey: ['opu-search', 'fert-data', params],
    enabled: !!token,
    async queryFn() {
      return fetchFertData(token, params);
    },
  });

export const statsOptions = (
  token: AccessToken,
  params: {
    aspirationDate: string;
    siteId: string;
    location?: string | null;
  },
) =>
  queryOptions({
    queryKey: ['opu-search', 'stats', params],
    enabled: !!token,
    async queryFn() {
      return fetchStats(token, params);
    },
  });

export const statsPseOptions = (
  token: AccessToken,
  params: {
    aspirationDate: string;
    siteId: string;
    location?: string | null;
  },
) =>
  queryOptions({
    queryKey: ['opu-search', 'stats-pse', params],
    enabled: !!token,
    async queryFn() {
      return fetchStatsPse(token, params);
    },
  });

export const entriesOptions = (
  token: AccessToken,
  params: {
    aspirationDate: string;
    siteId: string;
    techId: string;
  },
) =>
  queryOptions({
    queryKey: ['opu-search', 'entries', params],
    enabled: !!token,
    async queryFn() {
      return fetchEntries(token, params);
    },
  });

export const entriesPseOptions = (
  token: AccessToken,
  params: {
    aspirationDate: string;
    siteId: string;
    pseId: string;
  },
) =>
  queryOptions({
    queryKey: ['opu-search', 'entries-pse', params],
    enabled: !!token,
    async queryFn() {
      return fetchEntriesPse(token, params);
    },
  });

export const techAveragesOptions = (
  token: AccessToken,
  params: {
    aspirationDate: string;
    siteId: string;
    techId: string;
  },
) =>
  queryOptions({
    queryKey: ['opu-search', 'tech-averages', params],
    enabled: !!token,
    async queryFn() {
      return fetchTechAverages(token, params);
    },
  });

export const averagesOptions = (
  token: AccessToken,
  params: {
    aspirationDate: string;
    siteId: string;
  },
) =>
  queryOptions({
    queryKey: ['opu-search', 'averages', params],
    enabled: !!token,
    async queryFn() {
      return fetchAverages(token, params);
    },
  });

export const poolOptions = (
  token: AccessToken,
  params: {
    aspirationDate: string;
    siteId: string;
  },
) =>
  queryOptions({
    queryKey: ['opu-search', 'pool', params],
    enabled: !!token,
    async queryFn() {
      return fetchPool(token, params);
    },
  });

export const poolMultiSiteOptions = (
  token: AccessToken,
  params: {
    aspirationDate: string;
    siteIds: string[];
  },
) =>
  queryOptions({
    queryKey: ['opu-search', 'pool-multi-site', params],
    enabled: !!token && !!params.aspirationDate && params.siteIds.length > 0,
    async queryFn() {
      return fetchPoolMultiSite(token, params);
    },
  });

export const aspPoolsOptions = (
  token: AccessToken,
  params: { poolNumbers: number[] },
) =>
  queryOptions({
    queryKey: ['opu-search', 'pools', params],
    enabled: !!token && params.poolNumbers.length > 0,
    async queryFn() {
      return fetchAspPools(token, params.poolNumbers);
    },
  });

export const qcDataOptions = (
  token: AccessToken,
  params: {
    date: string;
    siteId: string;
    location?: string | null;
  },
) =>
  queryOptions({
    queryKey: ['opu-search', 'qc', params],
    enabled: !!token,
    async queryFn() {
      return fetchQcData(token, params);
    },
  });

export const showStopSignOptions = (token: AccessToken, siteId: string) =>
  queryOptions({
    queryKey: ['opu-search', 'stop-sign', siteId],
    enabled: !!token,
    async queryFn() {
      return fetchShowStopSign(token, siteId);
    },
  });

export function useRefreshStopGoSignMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: { siteId: string; val: string }) =>
      refreshStopGoSignFn(token, params),
  });
}

export function useUpdateAspirationDataEntryMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: {
      input: EmbryologistDataEntryRequest;
      isReturn: string;
    }) => updateAspirationDataEntryFn(token, params),
  });
}

export function useCreateAspPoolMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (request: CreateAspPoolRequest) =>
      createAspPoolFn(token, request),
  });
}

export function useRemoveAspPoolMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (request: RemoveAspPoolRequest) =>
      removeAspPoolFn(token, request),
  });
}

export function useUpdateQcDataMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: {
      input: QcDataUpdateRequest;
      aspirationDate: string;
      siteId: string;
      location?: string | null;
    }) => updateQcDataFn(token, params),
  });
}
